// Review with Claude, the words and the rules the page needs (brief 48, ADR 0030 decision 5). The
// command runs where the person already keeps their keys: an organisation's host with someone
// signed in, and the desktop app with or without an organisation. Anywhere else, align.interplae.com
// included, it says to sign in, and nothing is sent. The key is one of the person's Provider Keys
// for Anthropic (src/core/keys.ts, brief 47): one key, that one; several, the one picked once.

import { formatCost, toMicro } from '../../core/machine.ts'
import type { ProviderKey } from '../../core/keys.ts'
import type { Estimate, ReviewProgress, ReviewRequest } from './types.ts'

/// Where the page runs, for the review: the desktop app; an organisation's host with a member
/// signed in, whose role brief 49's Send to memory reads; or anywhere else, with whether it is an
/// organisation's host (signed out there) or a page with no organisation behind it.
export type Place =
  | { kind: 'desktop' }
  | { kind: 'member'; role: 'admin' | 'manager' | 'user' }
  | { kind: 'elsewhere'; organisation: boolean }

/// The words the command answers with anywhere it does not run.
export const SIGN_IN = 'Sign in to your organisation’s account to use this feature.'
export const SAVE_FIRST = 'Save the session file first, then open it here after signing in: this page’s autosave stays behind in this browser.'

/// The shared sign-in (ADR 0027 decision 5), which lands on the organisation's /align: on an
/// organisation's host its own hand-off page, and elsewhere interplae.com's list of organisations.
export function signInLink(place: Place): string {
  return place.kind === 'elsewhere' && place.organisation ? '/handoff?then=align' : 'https://interplae.com/organisations?then=align'
}

/// The key choice is remembered under this name, as a machine translation profile's is by its id.
export const CHOICE = 'align.review'

/// Anthropic's Provider Keys, and which one the review uses: the one key, the one picked while it
/// is still there, the keys to choose among, or none.
export type KeyFound = { kind: 'key'; key: ProviderKey } | { kind: 'choose'; keys: ProviderKey[] } | { kind: 'none' }

export function anthropicKey(keys: readonly ProviderKey[], chosen: string | null): KeyFound {
  const fits = keys.filter(k => k.kind === 'anthropic')
  const picked = fits.find(k => k.id === chosen)
  if (picked) return { kind: 'key', key: picked }
  if (fits.length === 1) return { kind: 'key', key: fits[0]! }
  if (fits.length > 1) return { kind: 'choose', keys: fits }
  return { kind: 'none' }
}

const MODELS: Record<string, string> = { 'claude-sonnet-5-5': 'Claude Sonnet 5.5', 'claude-opus-5-5': 'Claude Opus 5.5', 'claude-haiku-5-5': 'Claude Haiku 5.5', 'claude-fable-5-1': 'Claude Fable 5.1' }
export const modelName = (model: string) => MODELS[model] ?? model

const plural = (n: number, one: string, many = `${one}s`) => `${n.toLocaleString('en')} ${n === 1 ? one : many}`

/// The consent statement, machine translation's adapted to the review: what leaves, to whom, with
/// which key, and what does not.
export function consent(key: ProviderKey, estimate: Estimate): string {
  return `The rows below go to Anthropic with your key “${key.name}”. Requests go to ${estimate.host}, model ${modelName(estimate.model)}, in ${plural(estimate.windows, 'window')} of about 40 rows, one after another. Each request carries the source and target text of its rows and how the aligner paired them. Locked rows are not sent.`
}

/// The estimated cost, as machine translation reckons a call's: the tokens at the model's price.
export function costWords(estimate: Estimate): string {
  const tokens = `${plural(estimate.input_tokens, 'input token')} and ${plural(estimate.output_tokens, 'output token')}`
  if (estimate.cost_usd === null) return `About ${tokens}. This model has no price here, so the cost is not estimated.`
  return `Estimated cost: about ${formatCost(toMicro(estimate.cost_usd))}, for about ${tokens}. The provider bills your key for what it counts.`
}

export const progressWords = (p: ReviewProgress) => `${p.done.toLocaleString('en')} of ${plural(p.total, 'window')}`

/// The request with the key where its slot says. The review's requests name a header.
export function withKey(request: ReviewRequest, secret: string): { url: string; init: RequestInit } {
  const headers: Record<string, string> = Object.fromEntries(request.headers)
  const slot = request.credential
  if (!slot || slot.kind !== 'header') throw new Error('the review sends its key in a header')
  headers[slot.name] = slot.prefix + secret
  // Anthropic answers a page's own request only when it says it knows the key is in the page.
  headers['anthropic-dangerous-direct-browser-access'] = 'true'
  return { url: request.url, init: { method: request.method, headers, body: request.body } }
}

/// Every occurrence of the key replaced, so a reflected value never reaches the engine.
export const redact = (text: string, secret: string) => (secret ? text.split(secret).join('[redacted]') : text)
